












































































































import Vue from "vue";
import Modal from "@/components/root/modal/Modal.vue";
import { ModalConfig, UseModal } from "@/components/root/modal";
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { UseLoading } from "@/common/composition/loading";
import { ReportTemplateSection } from "../data/pdf/model";
import { clone } from "@/fp";
import { state as data_source_state } from "@/views/customize/data-sources/data/state";
import { DataSourceType } from "@/views/customize/data-sources/data/model";
import { pdf_state } from "@/views/reports/data/pdf/state.pdf";
import { TimeFrameOption } from "@/common/state/models";
import DropDownSimple from "@/components/dropdown/DropDownSimple.vue";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { state as ds_state } from "@/views/customize/data-sources/data/state";
import AppToggleBtn from "@/components/AppToggleBtn.vue";
import { SharedState } from "@/common/state";
import {
  getAggregationTimeFrameOptions,
  allowedAggregationTypes,
  isSelfContainedDeck,
  AggregationTimeFrameOption,
  isAggregationTimeFrameOption,
} from "../data/pdf/aggregation-timeframes";
import moment from "moment";

export default Vue.extend({
  components: { Modal, DropDownSimple, AppToggleBtn },
  props: ["modalData"],
  setup(props, { emit }) {
    const config: ModalConfig = {
      title: "",
      desc: "",
      blank: true,
      width: "65rem",
      height: "60rem",
      withTopRightCloseBtn: true,
    };

    const report_name = ref("");
    const is_aggregated = ref(false);

    const report_template_id = ref("");
    const [is_loading, set_loading] = UseLoading(false).as_arr();
    const displayed_sections = ref<ReportTemplateSection[]>([]);
    const [
      timeframes,
      selected_timeframe,
      set_selected_timeframe,
      init_timeframes,
    ] = UseDropdownItems<TimeFrameOption & { id: string; name: string }>([]);

    const org_start_date = computed(() => {
      const from = SharedState.basedOn.value?.dateFrom;
      if (from && typeof from === "string") return from;
      return moment().subtract(2, "years").format("YYYY-MM-DD");
    });

    const is_form_valid = computed(() => {
      return report_name.value.length > 0 && selected_timeframe.value != null;
    });

    const generate_report = async () => {
      const sel = selected_timeframe.value;
      if (!sel) return;

      const is_agg =
        is_aggregated.value && isAggregationTimeFrameOption(sel);

      let sections_to_send: import("../data/pdf/model").GenerateReportTemplateSection[];

      if (is_agg) {
        const agg_sel = sel as AggregationTimeFrameOption;
        // Display oldest → newest month pages
        const months = [...agg_sel.aggregation_months].reverse();
        const summary_display_name =
          months.length > 0
            ? `${agg_sel.displayName} (${moment(months[0], "YYYY-MM").format(
                "MMMM YYYY"
              )} - ${moment(months[months.length - 1], "YYYY-MM").format(
                "MMMM YYYY"
              )})`
            : agg_sel.displayName;

        // @ts-ignore — parameters_values Partial mismatch and TimeAxisType cast are safe here
        sections_to_send = displayed_sections.value.flatMap((s) => {
          // Build a unique toc_group key that includes parameter values so that
          // sections with the same question ID but different label variants
          // (Financial, Legal, HR, etc.) each get their own TOC entry.
          // `parameters_values` is a Partial<Record<...>>, so entries are typed
          // `{ id, name } | undefined`. At runtime Object.entries only yields
          // keys that are present, but an absent value still has to map to a
          // stable segment so two sections differing only in which parameter is
          // unset keep distinct keys.
          const toc_group_key =
            s.parameters_values && Object.keys(s.parameters_values).length > 0
              ? `${s.id}::${Object.entries(s.parameters_values)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([, v]) => (v ? v.id : ""))
                  .join("::")}`
              : s.id;

          const summarySection: import("../data/pdf/model").GenerateReportTemplateSection = {
            data_source: s.data_source,
            id: s.id,
            question: s.question,
            application: s.application,
            parameters: s.parameters,
            parameters_values: s.parameters_values as any,
            toc_group: toc_group_key,
            toc_group_is_summary: true,
            time: {
              type: "timeframe" as const,
              time_type: agg_sel.type as "Week" | "Month" | "Year",
              time_value: agg_sel.from,
              displayName: summary_display_name,
              aggregated: true,
              aggregation_type: agg_sel.aggregation_type,
              aggregation_value: agg_sel.aggregation_value,
              aggregation_months: agg_sel.aggregation_months,
            },
          };

          const monthSections: import("../data/pdf/model").GenerateReportTemplateSection[] = months.map((month_key) => {
            const month_moment = moment(month_key, "YYYY-MM");
            return {
              data_source: s.data_source,
              id: s.id,
              question: s.question,
              application: s.application,
              parameters: s.parameters,
              parameters_values: s.parameters_values as any,
              toc_group: toc_group_key,
              toc_group_is_summary: false,
              time: {
                type: "timeframe" as const,
                time_type: "Month" as const,
                time_value: month_moment.format("YYYY-MM-DD"),
                displayName: month_moment.format("MMMM YYYY"),
              },
            };
          });

          // A self-contained deck renders the whole report from its own
          // section, so the per-month expansion would repeat it once per
          // month (a quarter came out as the same 14 pages, four times).
          return isSelfContainedDeck(s.id)
            ? [summarySection]
            : [summarySection, ...monthSections];
        });
      } else {
        const timePayload = {
          type: "timeframe" as const,
          time_type: sel.type,
          time_value: sel.from,
          displayName: sel.displayName,
        };

        // @ts-ignore
        sections_to_send = displayed_sections.value.map((s) => ({
          data_source: s.data_source,
          id: s.id,
          question: s.question,
          application: s.application,
          parameters: s.parameters,
          parameters_values: s.parameters_values,
          time: timePayload,
        }));
      }

      pdf_state.generate_report_from_template({
        name: report_name.value,
        sections: sections_to_send,
        template_id: report_template_id.value,
      });
      emit("submit");
    };

    /** false for decks that take one period rather than an aggregation */
    const supports_aggregation = computed(
      () =>
        allowedAggregationTypes(displayed_sections.value.map((s) => s.id)) !==
        "none"
    );

    const apply_timeframes = () => {
      // Offer only the periods this report can actually render. The Quarterly
      // deck titles itself "Q2 2026 · Apr-Jun" from the months it receives and
      // rejects anything that is not a calendar quarter; the Monthly deck is a
      // single-month snapshot. Letting either be built against a rolling range
      // just produced a report that failed at generation time.
      const allowed = allowedAggregationTypes(
        displayed_sections.value.map((s) => s.id)
      );
      if (is_aggregated.value && allowed !== "none") {
        const opts = getAggregationTimeFrameOptions(
          org_start_date.value,
          allowed
        );
        init_timeframes(
          opts.map((d) => ({
            ...d,
            id: d.id,
            name: d.displayName,
          }))
        );
      } else {
        const frames = ds_state.ext.get_in_motion_source_time_frames(
          "motion_365"
        );
        init_timeframes(
          frames.map((d) => ({
            ...d,
            id: d.displayName,
            name: d.displayName,
          }))
        );
      }
      if (timeframes.value.length > 0) {
        set_selected_timeframe(timeframes.value[0].id);
      }
    };

    watch(is_aggregated, () => {
      apply_timeframes();
    });

    onMounted(() => {
      set_loading(true);
      apply_timeframes();
      // @ts-ignore
      const sections: ReportTemplateSection[] = props.modalData.sections;
      // @ts-ignore
      report_template_id.value = props.modalData.id;
      displayed_sections.value = clone(sections, true);
      for (const section of displayed_sections.value) {
        const source = data_source_state.all_sources.value.find(
          (s) => s.id == section.data_source
        )!;
        if (source.type == "InMotion") {
          const time_frames = data_source_state.ext.get_in_motion_source_time_frames(
            source.id
          );
          // @ts-ignore
          section.time = {
            type: "timeframe",
            time_type: time_frames[0].type,
            time_value: time_frames[0].from,
            displayName: time_frames[0].displayName,
          };
        } else {
          //
        }
      }
      set_loading(false);
    });

    return {
      config,
      is_form_valid,
      report_name,
      is_aggregated,
      supports_aggregation,
      timeframes,
      selected_timeframe,
      set_selected_timeframe,
      is_loading,
      sources: data_source_state.all_sources,
      displayed_sections,
      generate_report,
    };
  },
});
